/**
 * Pure logic for the inline `{{` variable typeahead. No DOM access here on
 * purpose — this module only knows about keys/strings/counts, so it can be
 * unit tested directly (see typeahead-reducer.test.ts) without a real
 * Froala/contentEditable instance.
 *
 * Trigger detection (`shouldOpenTypeahead`) is the one part that still runs
 * from the editor's own keydown handler (Froala.tsx) — that's what opens the
 * popover. Once open, focus moves to the popover's own search input (see
 * InlineVariableTypeahead) and query typing is native `<input>` behavior
 * (onChange), not editor keystrokes. `reduceSearchKeydown` below is that
 * input's onKeyDown logic — kept here, separate from the component, for the
 * same testability reason.
 */

export type SearchCloseReason = "escape" | "no-match";

export interface SearchNavResult {
  /** Highlight index to apply (unchanged from the input when this key didn't move it). */
  highlightedIndex: number;
  /** True when the highlighted row should be inserted (Enter/Tab with a match). */
  select: boolean;
  /** Present when the caller should close the popover without inserting. */
  closeReason?: SearchCloseReason;
  /** True when the caller must call `e.preventDefault()` on this keydown. */
  preventDefault: boolean;
}

/**
 * Does this keystroke complete a `{{` trigger? `charBeforeCaret` is whatever
 * currently sits immediately to the left of the caret, read BEFORE this
 * keystroke's default action lands (keydown fires pre-insertion).
 */
export const shouldOpenTypeahead = (
  key: string,
  charBeforeCaret: string | null,
): boolean => key === "{" && charBeforeCaret === "{";

/**
 * Reduces one keydown on the typeahead's own search input. `matchCount` is
 * the length of the currently filtered variable list — needed to bound
 * Up/Down and to know whether Enter/Tab have anything to insert. Every key
 * not handled here (character input, Backspace, ArrowLeft/Right, Home/End, …)
 * is left untouched: normal `<input>` text-editing behavior applies and the
 * query updates through the input's own `onChange`, not through this reducer.
 */
export const reduceSearchKeydown = (
  highlightedIndex: number,
  key: string,
  matchCount: number,
): SearchNavResult => {
  if (key === "Escape") {
    return { highlightedIndex, select: false, closeReason: "escape", preventDefault: true };
  }

  if (key === "ArrowDown") {
    if (matchCount === 0) return { highlightedIndex, select: false, preventDefault: true };
    return {
      highlightedIndex: (highlightedIndex + 1) % matchCount,
      select: false,
      preventDefault: true,
    };
  }

  if (key === "ArrowUp") {
    if (matchCount === 0) return { highlightedIndex, select: false, preventDefault: true };
    return {
      highlightedIndex: (highlightedIndex - 1 + matchCount) % matchCount,
      select: false,
      preventDefault: true,
    };
  }

  if (key === "Enter" || key === "Tab") {
    if (matchCount === 0) {
      return { highlightedIndex, select: false, closeReason: "no-match", preventDefault: false };
    }
    return { highlightedIndex, select: true, preventDefault: true };
  }

  return { highlightedIndex, select: false, preventDefault: false };
};
